import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TemplatePreviewModal = ({ template, onClose, onUseTemplate, hasActiveSubscription }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!template) return null;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? template?.screenshots?.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === template?.screenshots?.length - 1 ? 0 : prev + 1
    );
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-card rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground truncate">
              {template?.name}
            </h2>
            {template?.featured && (
              <span className="flex-shrink-0 px-2.5 py-1 bg-accent text-accent-foreground rounded-full text-xs font-medium flex items-center gap-1">
                <Icon name="Star" size={12} />
                Featured
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 ml-4 p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-250"
            aria-label="Close preview"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 p-6 md:p-8">
            <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
              <Image
                src={template?.screenshots?.[currentImageIndex]?.url}
                alt={template?.screenshots?.[currentImageIndex]?.alt}
                className="w-full h-full object-cover"
              />
              
              {template?.screenshots?.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center text-foreground hover:bg-background transition-all duration-250"
                    aria-label="Previous screenshot"
                  >
                    <Icon name="ChevronLeft" size={20} />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 backdrop-blur-sm rounded-full flex items-center justify-center text-foreground hover:bg-background transition-all duration-250"
                    aria-label="Next screenshot"
                  >
                    <Icon name="ChevronRight" size={20} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    {template?.screenshots?.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-250 ${
                          index === currentImageIndex
                            ? 'bg-primary w-6' :'bg-background/60'
                        }`}
                        aria-label={`Go to screenshot ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-3">
                  Description
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {template?.fullDescription}
                </p>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-3">
                  Key Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {template?.features?.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-sm text-foreground"
                    >
                      <Icon name="Check" size={16} className="text-success flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-3">
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {template?.technologies?.map((tech, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-muted text-foreground rounded-lg text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {!hasActiveSubscription && (
                <div className="p-4 bg-accent/10 border border-accent/20 rounded-lg flex items-start gap-3">
                  <Icon name="Crown" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      Subscription Required
                    </p>
                    <p className="text-sm text-muted-foreground">
                      This template is included with our monthly subscription. Subscribe to unlock access to all templates.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="X"
                  iconPosition="left"
                  onClick={onClose}
                  className="flex-1">
                  Close
                </Button>
                <Button
                  variant="default"
                  size="lg"
                  iconName={hasActiveSubscription ? "Rocket" : "Lock"}
                  iconPosition="left"
                  onClick={() => {
                    onUseTemplate(template);
                    onClose();
                  }}
                  className="flex-1">
                  {hasActiveSubscription ? "Use This Template" : "Subscribe to Use"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;